import React, {useMemo, useState} from "react";

import {Button} from "flowbite-react";
import {
  SurveyQuestionPage_ModelMapById,
  SurveyQuestionPage_RenderableModel,
  UserSurveyRenderableModel
} from "data/models/survey";
import InputTextSurveyQuestionPage from "./survey-question-pages/InputTextSurveyQuestionPage";
import VisualOptionsGraphicSurveyQuestionPage from "./survey-question-pages/VisualOptionsGraphicSurveyQuestionPage";
import RankOrderSurveyQuestionPage from "./survey-question-pages/RankOrderSurveyQuestionPage";
import FileUploadSurveyQuestionPage from "./survey-question-pages/FileUploadSurveyQuestionPage";
import {UserSurveyDatabaseSetterValue} from "data/access/surveys";
import UserLoginSurveyQuestionPage from "./survey-question-pages/UserLoginSurveyQuestionPage";
import {UserLoginValue} from "data/models/survey/login";
import {createUserWithEmailAndPassword, getAuth, sendSignInLinkToEmail, updateProfile} from "firebase/auth";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {generateRandomString} from "helpers/random-string";
import SingleChoiceSelectSurveyQuestionPage from "./survey-question-pages/SingleChoiceSelectSurveyQuestionPage";
import MultiChoiceSelectSurveyQuestionPage from "./survey-question-pages/MultiChoiceSelectSurveyQuestionPage";
import {ChoiceValue} from "data/models/survey/choice-base";
import {MultiChoiceSelect_SurveyQuestionPage_RenderableModel, MultiSelectValue} from "data/models/survey/multi-choice";
import BrandTraitsSurveyQuestionPage from "./survey-question-pages/BrandTraitsSurveyQuestionPage";
import FontSelectSurveyQuestionPage from "./survey-question-pages/FontSelectSurveyQuestionPage";
import {SelectedColors} from "data/models/survey/colors";
import ParagraphsSurveyQuestionPage from "./survey-question-pages/ParagraphsSurveyQuestionPage";
import {useSurveySectionsRenderingModel} from "data/access/survey-sections-rendering-model";
import {DESCRIPTION_CLASS, TITLE_CLASS} from "./classes";
import ViewBrandStylesSurveyQuestionPage from "./survey-question-pages/ViewBrandStylesSurveyQuestionPage";
import ColorsSelectSurveyQuestionPage from "./survey-question-pages/ColorsSelectSurveyQuestionPage";
import Colors2SelectSurveyQuestionPage from "./survey-question-pages/Colors2SelectSurveyQuestionPage";
import LoadingSurvey from "./LoadingSurvey";
import {QuestionKey} from "data/models/survey/keys";
import {QuestionKeyForNavigation, surveyQuestionPath} from "hooks/page-navigation/survey";
import {assertNoError} from "helpers/asserts";
import ClientLocationSurveyQuestionPage from "./survey-question-pages/ClientLocationSurveyQuestionPage";
import {ClientLocationValue} from "data/models/survey/client-location";
import {SingleSelectValue} from "data/models/survey/single-choice";
import clsx from "clsx";
import {TitleComponent} from "./survey-sections-rendering-components/SurveySection";
import {StorageFileValue} from "data/models/survey/file-upload";
import {FontDefinition} from "data/models/shared/recommended-fonts";
import Colors3SelectSurveyQuestionPage from "./survey-question-pages/Colors3SelectSurveyQuestionPage";
import {useAuthState} from "react-firebase-hooks/auth";
import {httpsCallable} from "firebase/functions";
import {SendTransactionalEmailRequest} from "data/models/shared/send-transactional-email";
import {functions} from "hooks/firebase";

type SurveyProps = {
  survey: UserSurveyRenderableModel;
  questionIdForNavigation: QuestionKeyForNavigation;
  previousSurveyButtonTitle: string;
  navigateToPreviousUserSurvey: () => void;
  nextSurveyButtonTitle: string;
  navigateToNextUserSurvey: () => void;
  onChange: (id: QuestionKey, value: UserSurveyDatabaseSetterValue) => Promise<void>;
  setSaveFormCallback: ((saveFormCallback: () => Promise<boolean>) => void)
}

type UserSurveyFormValue = UserSurveyDatabaseSetterValue;

type UserSurveyFormModel = {
  [key in QuestionKey]: UserSurveyFormValue
}

const Survey = (
    {
      survey: {
        surveyQuestionPages,
      },
      questionIdForNavigation,
      previousSurveyButtonTitle,
      navigateToPreviousUserSurvey,
      nextSurveyButtonTitle,
      navigateToNextUserSurvey,
      onChange,
      setSaveFormCallback,
    }: SurveyProps
) => {
  const auth = getAuth();
  const [user, loadingUser, errorUser] = useAuthState(auth);
  const [surveySectionsRenderingModel, surveySectionsRenderingModelLoading, surveySectionsRenderingModelError] = useSurveySectionsRenderingModel();

  const questionIds = useMemo(() => surveyQuestionPages.map(s => s.id), [surveyQuestionPages]);
  const defaultQuestionIndex = useMemo((): number => {
    switch (questionIdForNavigation) {
      case "first":
        return 0;
      case "last":
        return surveyQuestionPages.length - 1;
      default:
        const questionIndex = questionIds.indexOf(questionIdForNavigation);
        if (questionIndex === -1) {
          throw new Error(`questionId ${questionIdForNavigation} not found`);
        }
        return questionIndex;
    }
  }, [questionIdForNavigation, questionIds]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(defaultQuestionIndex);
  const currentQuestion = surveyQuestionPages[Math.min(currentQuestionIndex, surveyQuestionPages.length - 1)];
  const initialFormData = useMemo(() => surveyQuestionPages
          .filter(question => question.value !== undefined)
          .reduce(
              (result, question) => ({
                ...result,
                [question.id]: question.value as UserSurveyFormValue,
              }),
              {} as UserSurveyFormModel)
      , [surveyQuestionPages]
  );
  const [formData, setFormData] = useState(initialFormData);
  const [message, setMessage] = useState<string | null>(null);

  const questionsById: SurveyQuestionPage_ModelMapById = surveyQuestionPages.reduce((result, question) => ({
    ...result,
    [question.id]: question,
  }), {} as SurveyQuestionPage_ModelMapById);

  setSaveFormCallback(saveCurrentQuestionValue);

  function handleChange(id: string, value: UserSurveyFormValue) {
    setFormData({
      ...formData,
      [id]: value,
    });
  }

  function isCurrentQuestionValueSet(): boolean {
    const currentQuestionValue = formData?.[currentQuestion.id];
    return !(
        isNullOrUndefined(currentQuestionValue)
        || (typeof currentQuestionValue === "string" && currentQuestionValue === "")
        || (currentQuestionValue instanceof Array && currentQuestionValue.length === 0)
    );
  }


  const navigationButtonDisabled = !(
      (currentQuestion.required && isCurrentQuestionValueSet())
      || !currentQuestion.required
  ) || message !== null;

  function renderQuestionComponent(
      {
        id: questionId,
        title,
        description,
        kind,
        required,
        options,
      }: SurveyQuestionPage_RenderableModel,
      visible: boolean,
  ) {
    const renderedTitle = <TitleComponent title={title} surveySectionRenderingModel={surveySectionsRenderingModel}
                                          defaultTitleClass={TITLE_CLASS}/>;
    const renderedDescription = description !== null ?
        <TitleComponent title={description} surveySectionRenderingModel={surveySectionsRenderingModel}
                        defaultTitleClass={DESCRIPTION_CLASS}/> : null;
    switch (kind) {
      case "paragraphs":
        return <ParagraphsSurveyQuestionPage
            visible={visible}
            options={options}
            questionId={questionId}
            required={required}
            title={renderedTitle}
            description={renderedDescription}
            surveySectionsRenderingModel={surveySectionsRenderingModel}
            value={formData?.[questionId] as "visited" | undefined}
            onChange={handleChange}
        />
      case "input":
        return <InputTextSurveyQuestionPage
            options={options}
            questionId={questionId}
            required={required}
            title={renderedTitle}
            description={renderedDescription}
            surveySectionsRenderingModel={surveySectionsRenderingModel}
            value={formData?.[questionId] as string | undefined}
            onChange={handleChange}
        />
      case "single-choice-select":
        return <SingleChoiceSelectSurveyQuestionPage
            options={options}
            questionId={questionId}
            title={renderedTitle}
            description={renderedDescription}
            value={formData?.[questionId] as SingleSelectValue | undefined}
            onChange={handleChange}
        />
      case "multi-choice-select":
        return <MultiChoiceSelectSurveyQuestionPage
            options={options}
            questionId={questionId}
            title={renderedTitle}
            description={renderedDescription}
            surveySectionsRenderingModel={surveySectionsRenderingModel}
            value={formData?.[questionId] as MultiSelectValue | undefined}
            onChange={handleChange}
        />
      case "visual-options-graphic":
        return <VisualOptionsGraphicSurveyQuestionPage
            options={options}
            questionId={questionId}
            title={renderedTitle}
            description={renderedDescription}
            value={formData?.[questionId] as string[] | undefined}
            onChange={handleChange}
        />
      case "rank-order":
        return <RankOrderSurveyQuestionPage
            options={options}
            questionId={questionId}
            title={renderedTitle}
            description={renderedDescription}
            choices={getChoicesFromQuestions(options.choicesSources)}
            value={formData?.[questionId] as ChoiceValue[] | undefined}
            onChange={handleChange}
        />
      case "file-upload":
        return <FileUploadSurveyQuestionPage
            visible={visible}
            questionId={questionId}
            required={required}
            title={renderedTitle}
            description={renderedDescription}
            value={formData?.[questionId] as StorageFileValue | undefined}
            onChange={handleChange}
        />
      case "user-login":
        return <UserLoginSurveyQuestionPage
            options={options}
            questionId={questionId}
            value={formData?.[questionId] as UserLoginValue}
            onChange={handleChange}
        />
      case "brand-traits":
        return <BrandTraitsSurveyQuestionPage
            visible={visible}
            title={renderedTitle}
            description={renderedDescription}
            questionId={questionId}
            value={formData?.[questionId] as "visited" | undefined}
            onChange={handleChange}
        />
      case "fonts":
        return <FontSelectSurveyQuestionPage
            visible={visible}
            questionId={questionId}
            title={renderedTitle}
            surveySectionsRenderingModel={surveySectionsRenderingModel}
            value={formData?.[questionId] as FontDefinition | undefined}
            onChange={handleChange}
        />
      case "colors":
        return <ColorsSelectSurveyQuestionPage
            visible={visible}
            questionId={questionId}
            value={formData?.[questionId] as SelectedColors | undefined}
            onChange={handleChange}
        />
      case "colors2":
        return <Colors2SelectSurveyQuestionPage
            visible={visible}
            questionId={questionId}
            value={formData?.[questionId] as SelectedColors | undefined}
            onChange={handleChange}
        />
      case "colors3":
        return <Colors3SelectSurveyQuestionPage
            visible={visible}
            questionId={questionId}
            title={renderedTitle}
            value={formData?.[questionId] as SelectedColors | undefined}
            onChange={handleChange}
        />
      case "view-brand-styles":
        return <ViewBrandStylesSurveyQuestionPage
            visible={visible}
            title={renderedTitle}
            description={renderedDescription}
            surveySectionsRenderingModel={surveySectionsRenderingModel}
        />

      case "client-location":
        return <ClientLocationSurveyQuestionPage
            options={options}
            questionId={questionId}
            required={required}
            title={renderedTitle}
            value={formData?.[questionId] as ClientLocationValue | undefined}
            onChange={handleChange}
        />


      default:
        throw new Error(`Unhandled survey question kind: ${kind}`);
    }
  }

  function getChoicesFromQuestions(choicesSources: QuestionKey[]): ChoiceValue[] {
    function transformChoices(sourceQuestionId: QuestionKey, choices: ChoiceValue[]): ChoiceValue[] {
      if (sourceQuestionId !== "hopes-aspirations-goals") {
        return choices;
      }
      return choices.map(({key, value}) => ({
        key,
        value: `want ${value}`,
      }))
    }

    const sourcesThatHaveValues = choicesSources
        .filter(sourceQuestionId => formData?.[sourceQuestionId]);
    const valueChoicesObjects = sourcesThatHaveValues
        // Combine choices and values form the question into a single object
        .map(sourceQuestionId => ({
          choices: transformChoices(sourceQuestionId, (questionsById[sourceQuestionId] as MultiChoiceSelect_SurveyQuestionPage_RenderableModel).options.choices),
          value: (formData[sourceQuestionId] as MultiSelectValue)
        }));

    const choices = valueChoicesObjects.map(questionValue => [
      // map values into choices
      ...(questionValue.value?.choice?.values !== undefined ?
          questionValue.value.choice.values
              .map(value => questionValue.choices
                  .find(choice => choice.key === value)) as ChoiceValue[]
          : []),
      // convert other value into Choice object
      ...(questionValue.value?.choice?.other !== undefined ? [{
        key: questionValue.value.choice.other,
        value: questionValue.value.choice.other,
      }] : []),
    ]).flat();
    const choicesKeys = choices.map(choice => choice.key);
    return choices.filter((item, index) => choicesKeys.indexOf(item.key) === index);
  }

  async function signInUser({email, name}: UserLoginValue): Promise<boolean> {
    const sendTransactionalEmailFunction = httpsCallable<SendTransactionalEmailRequest, void>(functions, 'sendTransactionalEmail');

    await sendTransactionalEmailFunction({
      emailKind: "sign-in-link",
      recipient: {
        email,
        name,
      },
      data: {
        Company: formData["business-name"] as string | undefined ?? null,
        fromParam: "",
        appUrl: window.location.origin,
        linkDomain: window.location.host,
      }
    });

    window.localStorage.setItem('emailForSignIn', email);

    setMessage("You already have an account. We sent you an email with the login link. " +
        "Please click on the link to log in and continue.")

    return false;
  }

  async function createUser(userLogin: UserLoginValue) {
    // For some reason in react resets the state after the user is logged in and that makes the app navigate to the first page.
    localStorage.setItem("navigateToSurvey", surveyQuestionPath(
        "canva-assets",
        "all-features",
        "business-info",
        "business-description",
    ));
    try {
      // User with the email does not exist
      const userCredential = await createUserWithEmailAndPassword(auth, userLogin.email, generateRandomString(20));
      await updateProfile(userCredential.user, {
        displayName: userLogin.name,
      });

      const sendTransactionalEmailFunction = httpsCallable<SendTransactionalEmailRequest, void>(functions, 'sendTransactionalEmail');

      await sendTransactionalEmailFunction({
        emailKind: "welcome",
        recipient: userLogin,
        data: {
          Company: formData["business-name"] as string | undefined ?? null,
          fromParam: "",
          appUrl: window.location.origin,
          linkDomain: window.location.host,
        }
      });

    } catch (error) {
      // If error happens during the user creation we abandon the navigation to the description page too.
      localStorage.removeItem("navigateToSurvey");
      throw error;
    }
    return false;
  }

  async function saveCurrentQuestionValue(): Promise<boolean> {
    const questionId = currentQuestion.id;
    if (formData[questionId] === undefined) {
      return true;
    }
    const value = formData[questionId];

    async function loginUserIfLoginPage() {
      if (currentQuestion.kind !== "user-login" || !isNullOrUndefined(user)) {
        return true;
      }
      const userLogin = formData[currentQuestion.id] as UserLoginValue;

      try {
        return await createUser(userLogin);
      } catch (error: any) {
        if (error.code === "auth/email-already-in-use") {
          return await signInUser(userLogin);
        }
        throw error;
      }
    }

    await onChange(questionId, value);
    return await loginUserIfLoginPage();
  }

  async function previousQuestion() {
    const result = await saveCurrentQuestionValue();
    if (result) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  }

  async function nextQuestion() {
    const result = await saveCurrentQuestionValue();
    if (result) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  }

  async function previousSurvey() {
    const result = await saveCurrentQuestionValue();
    if (result) {
      setCurrentQuestionIndex(999);
      navigateToPreviousUserSurvey();
    }
  }

  async function nextSurvey() {
    const result = await saveCurrentQuestionValue();
    if (result) {
      setCurrentQuestionIndex(0);
      navigateToNextUserSurvey();
    }
  }

  assertNoError(errorUser ?? surveySectionsRenderingModelError);
  if (loadingUser || surveySectionsRenderingModelLoading) {
    return <LoadingSurvey/>;
  }

  return (
      <div className={"mt-8 w-full"}>
        <div className={"flex flex-row w-full"}>
          {surveyQuestionPages.map((question, index) => (
              <div
                  key={index}
                  className={clsx(
                      "w-full md:min-w-96 mx-auto",
                      question.maxScreenWidth === "sm" && "max-w-screen-sm",
                      question.maxScreenWidth === "md" && "max-w-screen-md",
                      question.maxScreenWidth === "lg" && "max-w-screen-lg",
                      question.maxScreenWidth === "xl" && "max-w-screen-xl",
                      question.maxScreenWidth === "2xl" && "max-w-screen-2xl",
                      currentQuestionIndex !== index && "hidden"
                  )}>
                {renderQuestionComponent(question, currentQuestionIndex === index)}
                <div className={"mt-8"}>
                  {message !== null &&
                      <div className={"my-12"}><span className={DESCRIPTION_CLASS}>{message}</span></div>}
                  <div className={"flex flex-row justify-center items-start"}>
                    <Button
                        className={"min-w-24 mr-1 rounded-full"}
                        color={"gray"}
                        onClick={async () => await (currentQuestionIndex === 0 ? previousSurvey : previousQuestion)()}
                    >{question.backButtonText ?? (currentQuestionIndex === 0 ? previousSurveyButtonTitle : "Back")}</Button>
                    <Button
                        className={"min-w-24 ml-1 rounded-full"}
                        color={"blue"}
                        onClick={async () => await(currentQuestionIndex === surveyQuestionPages.length - 1 ? nextSurvey : nextQuestion)()}
                        disabled={navigationButtonDisabled}
                    >{question.nextButtonText ?? (currentQuestionIndex === surveyQuestionPages.length - 1 ? nextSurveyButtonTitle : "Next")}</Button>
                  </div>
                </div>
              </div>))}
        </div>
      </div>
  )
};

export default Survey;
